import type { LensCardType } from '~/types/leLens';

import { LENS_GRAINS, parseGrain } from './lensGrain';

/**
 * The finest grain a Lens dataset stores today is a day: lensGrain.ts's own
 * comment says a dataset returns days and a card's Group by buckets them
 * coarser from there. Day is not one of LENS_GRAINS because nothing groups
 * *up to* a day, but it is where every dataset starts, so it heads this list
 * rather than sitting outside it.
 */
export const RESOLVE_GRAINS = ['day', ...LENS_GRAINS] as const;

export type ResolveGrain = (typeof RESOLVE_GRAINS)[number];

/**
 * A grain given as a bare word ("month") or as the grain expression a card's
 * own view already uses ("month(order_date)"). Reusing parseGrain here means
 * this module never grows a second way to recognise a grain: a card and a
 * dataset both speak in whichever of the two forms parseGrain already reads.
 * Anything neither form recognises is taken as day, the grain nothing needs
 * to say out loud because it is where an unaggregated dataset already sits.
 */
export function normaliseGrain(value: string | undefined): ResolveGrain {
  const parsed = parseGrain(value);
  if (parsed) {
    return parsed.grain;
  }
  const bare = value?.trim().toLowerCase();
  return (RESOLVE_GRAINS as ReadonlyArray<string>).includes(bare ?? '') ? (bare as ResolveGrain) : 'day';
}

function grainRank(grain: ResolveGrain): number {
  return RESOLVE_GRAINS.indexOf(grain);
}

/**
 * A card the create journey's planner produced, still needing a source of
 * numbers. `sourceTable` and `columns` are the structured ask the plan
 * gate resolves against; `request`/`type` ride along only for the sentence
 * and are never scored. `suggestedDatasetId`, when the planner named one, is
 * a hint the caller can trace back, not a match this module takes on trust:
 * a suggested dataset still has to pass the same source, grain and column
 * checks as any other candidate, exactly as the plan endpoint's own response
 * is validated rather than trusted.
 */
export type ResolveCard = {
  title: string;
  type: LensCardType;
  request: string;
  sourceTable: string;
  grain: ResolveGrain;
  columns: ReadonlyArray<string>;
  suggestedDatasetId?: string;
  /** Set only while resolving cards being appended onto one specific
   *  Lens. A candidate already sitting there is the best reuse outcome
   *  there is, so the sentence says "this Lens" rather than repeating a
   *  title the reader is already looking at. */
  targetLensId?: string;
};

/**
 * One dataset already sitting on a dashboard the caller can read, in the
 * shape the list endpoint's full specs already carry once a dataset's id,
 * name and owning Lens are pulled out alongside it.
 */
export type ResolveDataset = {
  datasetId: string;
  datasetName: string;
  lensId: string;
  lensTitle: string;
  sourceTable: string;
  grain: ResolveGrain;
  columns: ReadonlyArray<string>;
};

export type ResolveReason = {
  sourceTable: string;
  grain: { card: ResolveGrain; dataset: ResolveGrain; rolledUp: boolean };
  columns: { required: number; covered: number; extra: number };
  /** True when this is the dataset the planner suggested for this card. */
  suggested: boolean;
};

export type ResolveCandidate = {
  dataset: ResolveDataset;
  /** 0 to 1. Ranking only: anything scored at all has already cleared every gate. */
  score: number;
  reason: ResolveReason;
  sentence: string;
};

export type CardResolution = {
  card: ResolveCard;
  /** Best first. Empty means nothing on a readable dashboard can answer this card. */
  candidates: ReadonlyArray<ResolveCandidate>;
  needsNewSql: boolean;
};

function normaliseKey(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Scores one dataset against one card, or refuses it outright.
 *
 * Three gates, all of which have to hold before a score means anything: the
 * same source table, a grain the dataset can actually serve, and every
 * column the card needs. A dataset failing any one of them cannot answer the
 * card no matter how well it scores on the others, so it is not a low-scoring
 * candidate, it is not a candidate.
 *
 * Grain compatibility is not symmetric. A dataset at or finer than the card's
 * grain can be grouped up to it for free; a dataset coarser than what the
 * card asks for has already thrown away the rows the card would need, and no
 * amount of grouping gets them back.
 */
export function scoreCandidate(card: ResolveCard, dataset: ResolveDataset): ResolveCandidate | undefined {
  if (normaliseKey(dataset.sourceTable) !== normaliseKey(card.sourceTable)) {
    return undefined;
  }

  const cardRank = grainRank(card.grain);
  const datasetRank = grainRank(dataset.grain);
  if (datasetRank > cardRank) {
    return undefined;
  }

  const datasetColumns = new Set(dataset.columns.map(normaliseKey));
  const missing = card.columns.filter((column) => !datasetColumns.has(normaliseKey(column)));
  if (missing.length) {
    return undefined;
  }

  const requiredCount = card.columns.length;
  const extra = dataset.columns.length - requiredCount;
  const rolledUp = datasetRank < cardRank;

  // Exact grain scores 1; each rollup step it takes to reach the card's grain
  // costs an even share of the range, so a dataset three steps finer than a
  // quarterly card still matches but ranks behind one already at quarter.
  const maxSteps = RESOLVE_GRAINS.length - 1;
  const grainScore = maxSteps === 0 ? 1 : 1 - (cardRank - datasetRank) / maxSteps;
  // A dataset carrying exactly the columns the card needs scores 1; one
  // carrying unrelated extra columns scores lower, since it is a noisier
  // reuse even though every required column is there.
  const columnScore = requiredCount === 0 ? 1 : requiredCount / dataset.columns.length;
  const score = Math.round((grainScore * 0.5 + columnScore * 0.5) * 1000) / 1000;

  const reason: ResolveReason = {
    sourceTable: dataset.sourceTable,
    grain: { card: card.grain, dataset: dataset.grain, rolledUp },
    columns: { required: requiredCount, covered: requiredCount, extra },
    suggested: card.suggestedDatasetId === dataset.datasetId,
  };

  return { dataset, score, reason, sentence: buildSentence(dataset, reason, card.targetLensId) };
}

function buildSentence(dataset: ResolveDataset, reason: ResolveReason, targetLensId?: string): string {
  const label = dataset.lensId === targetLensId ? 'this Lens' : dataset.lensTitle;
  if (!reason.grain.rolledUp) {
    return `Reuses a query from ${label} that already answers this at the ${reason.grain.card} grain.`;
  }
  return `Reuses a query from ${label} that already groups this by ${reason.grain.dataset}.`;
}

/**
 * Every acceptable candidate for one card, best first.
 *
 * Ties keep the order `datasets` arrived in: Array.prototype.sort is a stable
 * sort, so two equally-good candidates stay in whatever order the caller's
 * own dataset list already put them in rather than reordering on nothing.
 */
export function resolveCard(card: ResolveCard, datasets: ReadonlyArray<ResolveDataset>): CardResolution {
  const candidates = datasets
    .map((dataset) => scoreCandidate(card, dataset))
    .filter((candidate): candidate is ResolveCandidate => Boolean(candidate))
    .sort((a, b) => b.score - a.score);

  return { card, candidates, needsNewSql: candidates.length === 0 };
}

/**
 * Resolves every card in a plan against the same pool of readable datasets.
 *
 * One pool shared across every card, because the candidate pool is the set
 * of datasets on dashboards the user can read today, not something that
 * changes card to card.
 */
export function resolvePlan(
  cards: ReadonlyArray<ResolveCard>,
  datasets: ReadonlyArray<ResolveDataset>,
): ReadonlyArray<CardResolution> {
  return cards.map((card) => resolveCard(card, datasets));
}
